import { FC, useCallback, useState } from 'react';
import { forwardRef } from 'react';
import CoreScroll from '@nrk/core-scroll';

import { SwimlaneItem } from '@rikstv/play-common/src/forces/assetDetails/assetDetails.types';
import { useWindowSize } from '@rikstv/play-common/src/hooks/useWindowSize';
import { useSetScrollRestoration } from '@rikstv/play-common/src/router/useScrollRestoration';
import { isOneOf, RequiredProps } from '@rikstv/play-common/src/utils/types/typeUtils';
import { Gutter, useIntersectionObserver } from '@rikstv/shared-components';

import { FadeIn } from '../../../components/animation/fade-in/FadeIn';
import { useSwimlane } from '../../../components/swimlane/useSwimlane';
import { CoverPageGrid } from '../../coverPage/grid/CoverPageGrid';
import { Swimlane } from '../../pages/types';
import { ItemCountReporterFunction } from '../../pages/useEmptyCollectionReporting';
import { HeroSwimlaneItem } from '../heroSwimlane/HeroSwimlaneItem';
import { ContinueWatchingItem } from '../styles/ContinueWatchingItem';
import { FeaturedItem } from '../styles/FeaturedItem';
import { LiveChannelItem } from '../styles/LiveChannelItem';
import { MenuSwimlaneItem } from '../styles/MenuSwimlaneItem';
import { NumberedItem } from '../styles/NumberedItem';
import { PortraitItem } from '../styles/PortraitItem';
import { StandardItem } from '../styles/StandardItem';
import { SuperWideHeroItem } from '../styles/SuperWideHeroItem';
import { SwimlanePlaceholder } from '../swimlanePlaceholder/SwimlanePlaceholder';
import { HeroSwimlaneScroller } from '../swimlaneScroller/HeroSwimlaneScroller';
import { SwimlaneScroller } from '../swimlaneScroller/SwimlaneScroller';
import { getSwimlaneItemTypes } from '../utils/getSwimlaneItemTypes';

import { useImpressionTracking } from './useImpressionTracking';
import { useReportPlayExternalAsset } from './useReportPlayExternalAsset';

export interface CoreScrollSwimlaneProps {
  swimlane: Swimlane;
  swimlaneIndex?: number;
  onItemClicked?: (item: SwimlaneItem, index: number) => void;
  enableImpressionTracking?: boolean;
  reportSwimlaneItems: ItemCountReporterFunction;
}

export const CoreScrollSwimlane: FC<CoreScrollSwimlaneProps> = ({
  swimlane,
  swimlaneIndex = 0,
  onItemClicked,
  enableImpressionTracking = true,
  reportSwimlaneItems,
}) => {
  const { setRestoreScrollPoint } = useSetScrollRestoration();
  const [isInViewport, setIsInViewport] = useState(false);
  const { swimlaneItems, error, filterSwimlaneItemById, isPlaceholder } = useSwimlane(swimlane, isInViewport);
  const { setVisibleItems, reportSwimlaneClick } = useImpressionTracking(
    swimlaneItems,
    swimlane,
    swimlaneIndex,
    enableImpressionTracking
  );
  const reportExternalAppLaunch = useReportPlayExternalAsset();
  const nrOfItems = swimlaneItems?.length ?? 0;
  const onClick = useCallback(
    (item: SwimlaneItem, elementIndex: number) => {
      reportSwimlaneClick(item, elementIndex);
      onItemClicked?.(item, elementIndex);
      setRestoreScrollPoint(swimlane.id, elementIndex);
      reportExternalAppLaunch(item);
    },
    [reportSwimlaneClick, onItemClicked, setRestoreScrollPoint, swimlane.id, reportExternalAppLaunch]
  );

  const fallback = () => setIsInViewport(true);
  const [swimlaneTriggerRef] = useIntersectionObserver(
    list => {
      if (list.some(item => item.isIntersecting) && !isInViewport) {
        setIsInViewport(true);
      }
    },
    { rootMargin: '250px' },
    fallback
  );
  useCallback(() => {
    if (isInViewport) {
      reportSwimlaneItems(swimlane.id, nrOfItems);
    }
  }, [isInViewport, nrOfItems, reportSwimlaneItems, swimlane.id]);

  const reportIsVisible = useCallback(
    (target: HTMLElement) => {
      if (!swimlaneItems) {
        return;
      }

      const scroller = target as unknown as CoreScroll['el'];
      const elements = scroller.items;

      const visibleElements = elements
        .map((element, idx) => {
          const elementLeftCordinate = element.getBoundingClientRect().x;
          const windowWidth = window.innerWidth;

          if (elementLeftCordinate <= windowWidth - element.offsetWidth) {
            return swimlaneItems[idx];
          }
        })
        .filter((element): element is SwimlaneItem => !!element);

      setVisibleItems(currentVisibleItems => {
        if (currentVisibleItems.length !== visibleElements.length) {
          return visibleElements;
        }
        // if all elements in new array exist in old, return old to avoid re-render
        if (visibleElements.every(e => currentVisibleItems.includes(e))) {
          return currentVisibleItems;
        }
        return visibleElements;
      });
    },
    [setVisibleItems, swimlaneItems]
  );

  // Return null on error or when returned list has zero items
  if (error || swimlaneItems?.length === 0) {
    return null;
  }

  const swimlaneItemType = getSwimlaneItemTypes(swimlaneItems, swimlane.style, swimlane.type);

  if (!swimlaneItemType) {
    return null;
  }

  if (swimlaneItemType === 'heroItems') {
    return (
      <FadeIn animationDelay={swimlaneIndex}>
        <HeroMapper
          ref={swimlaneTriggerRef}
          swimlane={swimlane}
          swimlaneItems={swimlaneItems}
          onClick={onClick}
          reportIsVisible={reportIsVisible}
          isPlaceholder={isPlaceholder}
        />
      </FadeIn>
    );
  }

  if (isOneOf(swimlaneItemType, ['swimmingPoolItems', 'swimmingPoolPortraitItems'])) {
    return (
      <Gutter>
        <SwimlanePlaceholder active={isPlaceholder} ref={swimlaneTriggerRef}>
          <CoverPageGrid portrait={swimlaneItemType === 'swimmingPoolPortraitItems'}>
            {swimlaneItems.map((item, index) => {
              if (swimlaneItemType === 'swimmingPoolPortraitItems') {
                return (
                  <StandardItem
                    asset={item}
                    noMargin
                    swimlaneType={'Default'}
                    swimlaneStyle={swimlane.style}
                    key={swimlane.id + item.cardId}
                    onClick={() => onClick(item, index)}
                  />
                );
              }

              return (
                <FeaturedItem
                  asset={item}
                  noMargin
                  showDescription
                  swimlaneType={'Default'}
                  swimlaneStyle={swimlane.style}
                  key={swimlane.id + item.cardId}
                  onClick={() => onClick(item, index)}
                />
              );
            })}
          </CoverPageGrid>
        </SwimlanePlaceholder>
      </Gutter>
    );
  }

  return (
    <FadeIn animationDelay={swimlaneIndex}>
      <SwimlaneScroller
        ref={swimlaneTriggerRef}
        isPlaceHolder={isPlaceholder}
        reportVisibility={reportIsVisible}
        swimlane={swimlane}
        animate={swimlaneItemType === 'liveChannels'}
        hideSectionTitle={swimlane.hideName}>
        {/* menu items */}
        {swimlaneItemType === 'menuItems' &&
          swimlaneItems.map((item, index) => (
            <MenuSwimlaneItem
              key={swimlane.id + item.id}
              asset={item}
              swimlaneType={swimlane.type}
              swimlaneStyle={swimlane.style}
              onClick={() => onClick(item, index)}
              noMargin
            />
          ))}
        {/* standard items */}
        {swimlaneItemType === 'standardItems' &&
          swimlaneItems.map((item, index) => (
            <StandardItem
              key={'standard-item-' + item.cardId}
              asset={item}
              swimlaneType={swimlane.type}
              swimlaneStyle={swimlane.style}
              onClick={() => onClick(item, index)}
              noMargin
            />
          ))}
        {swimlaneItemType === 'superWideHeroItems' &&
          swimlaneItems.map((item, index) => (
            <SuperWideHeroItem key={'standard-item-' + item.cardId} item={item} onClick={() => onClick(item, index)} />
          ))}

        {swimlaneItemType === 'numberedItems' &&
          swimlaneItems.map((item, index) => (
            <NumberedItem
              index={index + 1}
              key={'standard-item-' + item.cardId}
              asset={item}
              swimlaneType={swimlane.type}
              swimlaneStyle={swimlane.style}
              onClick={() => onClick(item, index)}
              noMargin
            />
          ))}

        {/* featured items */}
        {swimlaneItemType === 'featuredItems' &&
          swimlaneItems.map((item, index) => (
            <FeaturedItem
              key={swimlane.id + item.cardId}
              asset={item}
              swimlaneType={swimlane.type}
              swimlaneStyle={swimlane.style}
              onClick={() => onClick(item, index)}
              noMargin
            />
          ))}

        {/* portraits */}
        {swimlaneItemType === 'portraitItems' &&
          swimlaneItems.map((item, index) => (
            <PortraitItem
              key={swimlane.id + item.cardId}
              asset={item}
              swimlaneType={swimlane.type}
              swimlaneStyle={swimlane.style}
              onClick={() => onClick(item, index)}
              noMargin
              hideCaption={swimlane.style === 'HeroPortrait'}
            />
          ))}

        {/* live channel items */}
        {swimlaneItemType === 'liveChannels' &&
          (swimlaneItems as RequiredProps<SwimlaneItem, 'channel'>[]).map((item, index) => (
            <LiveChannelItem asset={item} key={swimlane.id + item.cardId} onClick={() => onClick(item, index)} />
          ))}

        {/* continue watching items */}
        {swimlaneItemType === 'continueWatchingItems' &&
          swimlaneItems.map((item, index) => (
            <ContinueWatchingItem
              swimlane={swimlane}
              key={swimlane.id + item.cardId}
              asset={item}
              preventNavigation={false}
              setOptimisticFilter={filterSwimlaneItemById}
              onClick={() => onClick(item, index)}
            />
          ))}

        {/* MyList items */}
        {swimlaneItemType === 'myListItems' &&
          swimlaneItems.map((item, index) => (
            <StandardItem
              key={swimlane.id + item.cardId}
              asset={item}
              swimlaneType={swimlane.type}
              swimlaneStyle={swimlane.style}
              onClick={() => onClick(item, index)}
              noMargin
            />
          ))}
      </SwimlaneScroller>
    </FadeIn>
  );
};

const HeroMapper = forwardRef<
  HTMLDivElement,
  {
    swimlane: Swimlane;
    swimlaneItems: SwimlaneItem[];
    onClick: (item: SwimlaneItem, elementIndex: number) => void;
    reportIsVisible?: (target: HTMLElement) => void;
    isPlaceholder: boolean;
  }
>(({ swimlane, swimlaneItems, onClick, reportIsVisible, isPlaceholder }, ref) => {
  const { width } = useWindowSize();

  return (
    <div ref={ref}>
      <HeroSwimlaneScroller
        swimlane={swimlane}
        reportIsVisible={reportIsVisible}
        secondsBeforeScroll={8}
        isPlaceholder={isPlaceholder}
        hideSwimlaneTitle={swimlane.hideName}>
        {swimlaneItems.map((item, index) => (
          <HeroSwimlaneItem
            key={swimlane.id + item.cardId}
            item={item}
            windowWidth={width}
            onClick={() => onClick(item, index)}
          />
        ))}
      </HeroSwimlaneScroller>
    </div>
  );
});
